import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { App } from 'antd';
import { Upload, FileText, X } from 'lucide-react';
import ResumeEditorModal from '../components/ResumeEditorModal';
import AuthModal from '../components/AuthModal';
import { auth } from '../services/auth';
import { trackEditResume } from '../services/behaviorTracker';

// 验证文件类型
const validateResumeFile = (file: File): string | null => {
  const validExtensions = ['.txt', '.md', '.pdf', '.docx'];
  const fileExtension = file.name
    .toLowerCase()
    .slice(file.name.lastIndexOf('.'));

  if (!validExtensions.includes(fileExtension)) {
    return '不支持的文件格式，请上传 TXT、MD、PDF 或 DOCX 格式的文件';
  }

  return null;
};

// 文件上传区域组件
function UploadArea({
  onFileSelect,
  selectedFile,
  onRemoveFile,
  disabled,
}: {
  onFileSelect: (file: File) => void;
  selectedFile: File | null;
  onRemoveFile: () => void;
  disabled?: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const { message } = App.useApp();

  const handleDrag = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.type === 'dragenter' || e.type === 'dragover') {
        setDragActive(true);
      } else if (e.type === 'dragleave') {
        setDragActive(false);
      }
    },
    [disabled],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const file = e.dataTransfer.files?.[0];
      if (file) {
        const error = validateResumeFile(file);
        if (error) {
          message.error(error);
          return;
        }
        onFileSelect(file);
      }
    },
    [onFileSelect, message, disabled],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      const file = e.target.files?.[0];
      if (file) {
        const error = validateResumeFile(file);
        if (error) {
          message.error(error);
          return;
        }
        onFileSelect(file);
      }
    },
    [onFileSelect, message, disabled],
  );

  if (selectedFile) {
    return (
      <div
        className={`bg-slate-800/50 rounded-xl p-4 border border-slate-700 ${
          disabled ? 'opacity-60' : ''
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <FileText className="w-6 h-6 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium truncate">
              {selectedFile.name}
            </p>
            <p className="text-slate-500 text-sm">
              {(selectedFile.size / 1024).toFixed(1)} KB
            </p>
          </div>
          {!disabled && (
            <button
              onClick={onRemoveFile}
              className="p-2 text-slate-500 hover:text-amber-400 hover:bg-amber-500/20 rounded-lg transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => !disabled && fileInputRef.current?.click()}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      className={`relative border-2 border-dashed rounded-xl p-10 text-center transition-all duration-200 ${
        disabled
          ? 'border-slate-700 bg-slate-800/30 cursor-not-allowed opacity-60'
          : dragActive
            ? 'border-slate-500 bg-slate-800/50 cursor-pointer'
            : 'border-slate-600 hover:border-slate-500 hover:bg-slate-800/50 cursor-pointer'
      }`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".txt,.md,.docx,.pdf"
        onChange={handleFileChange}
        disabled={disabled}
        className="hidden"
      />
      <Upload className="w-7 h-7 mx-auto mb-3 text-slate-500" />
      <p className="text-white font-medium mb-2">点击或拖拽选择简历</p>
      <p className="text-slate-500 text-sm">支持 PDF、DOCX、TXT、MD 格式</p>
    </div>
  );
}

export default function ResumeEditPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>(
    'login',
  );

  const handleFileSelect = useCallback((file: File) => {
    setSelectedFile(file);
  }, []);

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
  }, []);

  const handleEdit = () => {
    if (!selectedFile) {
      message.error('请先上传简历文件');
      return;
    }

    // 检查用户是否已登录
    if (!auth.isAuthenticated()) {
      setAuthModalMode('login');
      setIsAuthModalOpen(true);
      return;
    }

    // 获取文件扩展名
    const fileExtension = selectedFile.name
      .toLowerCase()
      .slice(selectedFile.name.lastIndexOf('.') + 1);

    // 上报编辑简历埋点
    trackEditResume(fileExtension, {
      fileName: selectedFile.name,
      fileSize: selectedFile.size,
    });

    setShowEditor(true);
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#0B0F19] via-[#0F172A] to-[#1E293B] py-8 px-4"
      style={{ minHeight: '73vh' }}
    >
      <div className="max-w-2xl mx-auto mt-16">
        {/* 标题 */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">
            <span className="bg-gradient-to-r from-white via-amber-100 to-amber-200 bg-clip-text text-transparent">
              简历编辑
            </span>
          </h1>
          <p className="text-slate-400">在线编辑简历内容，支持多种格式</p>
        </div>

        {/* 上传区域 */}
        <div className="mb-6">
          <UploadArea
            onFileSelect={handleFileSelect}
            selectedFile={selectedFile}
            onRemoveFile={handleRemoveFile}
          />
        </div>

        {/* 开始编辑按钮 */}
        <div className="flex justify-center">
          <button
            onClick={handleEdit}
            disabled={!selectedFile}
            className={`
              relative w-[200px] py-3 rounded-xl font-medium text-base overflow-hidden
              transition-all duration-300
              ${
                selectedFile
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 hover:scale-[1.02] animate-gradient cursor-pointer'
                  : 'bg-slate-700 cursor-not-allowed'
              }
            `}
          >
            <span
              className={`relative z-10 flex items-center justify-center ${
                selectedFile ? 'text-white' : 'text-slate-500'
              }`}
            >
              开始编辑
            </span>
            {selectedFile && (
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-shimmer" />
            )}
          </button>
        </div>
      </div>

      {/* 简历编辑器模态框 */}
      <ResumeEditorModal
        file={selectedFile}
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        showEvaluationSidebar={false}
      />

      {/* 登录/注册弹窗 */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        defaultMode={authModalMode}
      />

      {/* CSS 动画定义 */}
      <style>{`
        @keyframes shimmer {
          100% {
            transform: translateX(100%);
          }
        }

        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        .animate-shimmer {
          animation: shimmer 2s infinite;
        }

        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </div>
  );
}
